//built-in libraries
import { useEffect, useState } from 'react';

//Third party libraries
import { useQuery } from '@tanstack/react-query';
import { InformationCircleIcon } from '@heroicons/react/outline';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationIcon,
} from '@heroicons/react/solid';

//Custom components
import Button from '../../../../UI/Button';

//services
import { getBulkUserInviteStatus } from '../../../../../api/Terminal/UserAccount';
import {
  IGetBulkUserInviteStatusServiceResData,
  TBulkUserInviteStatus,
} from '../../../../../api/Terminal/UserAccount/types';
import { IInvalidRecordsArray } from '../../../../../hooks/useParseCSV';

//Assets and Miscellaneous

interface IBulkUserInviteSummaryProps {
  handleModal: (modalName: 'inviteNewUser') => void;
  batchId: number;
  invalidRecords?: any;
}

//Component Begin
export default function BulkUserInviteSummary({
  batchId,
  handleModal,
  invalidRecords,
}: IBulkUserInviteSummaryProps) {
  const [perPage, setPerPage] = useState(10);
  const [inviteStatusParams, setInviteStatusParams] = useState<{
    perPage: number;
    orderBy: string;
    order: 'asc' | 'desc';
    status: TBulkUserInviteStatus | undefined;
  }>({
    perPage: 10,
    orderBy: 'id',
    order: 'desc',
    status: undefined,
  });

  const fetchInviteStatus = useQuery(
    ['get-upload-status', { batchId, inviteStatusParams }],
    () => getBulkUserInviteStatus({ batchId, params: inviteStatusParams }),
    {
      enabled: !!batchId,
    }
  );
  const inviteStatus = fetchInviteStatus?.data?.data!;
  const inviteStat = fetchInviteStatus?.data?.meta!;

  useEffect(() => {
    const fetchTimeout = setTimeout(() => {
      if (inviteStat?.pending > 0 || inviteStat?.processing > 0) {
        fetchInviteStatus.refetch();
      }
    }, 5000);

    return () => {
      clearTimeout(fetchTimeout);
    };
  }, [inviteStat, fetchInviteStatus]);

  const nextHandler = () => {
    if (invalidRecords?.length > perPage) {
      setPerPage((prevState) => prevState + 10);
    } else {
      if (inviteStat?.total > perPage) {
        setInviteStatusParams((prevState) => ({
          ...prevState,
          perPage: prevState.perPage + 10,
        }));
      }
    }
  };

  const statusClickHandler = (status: TBulkUserInviteStatus | undefined) => {
    setInviteStatusParams((prevState) => ({ ...prevState, status }));
  };

  return (
    <>
      <div className='grid grid-cols-3'>
        <div
          className='my-4 flex gap-2 items-center'
          onClick={() => statusClickHandler('COMPLETED')}
        >
          <CheckCircleIcon className='h-5 w-5 text-primaryColor-normal ' />
          <span className='cursor-pointer hover:underline hover:text-primaryColor-normal'>
            Success: {inviteStat?.completed ? inviteStat?.completed : 0}
          </span>
        </div>
        <div
          className='my-4 flex gap-2 items-center'
          onClick={() => statusClickHandler('FAILED')}
        >
          <ExclamationIcon className='h-5 w-5 text-tertiaryColor-normal ' />
          {invalidRecords && (
            <span className='cursor-pointer hover:underline hover:text-primaryColor-normal'>
              Failed:{' '}
              {inviteStat?.failed
                ? inviteStat?.failed + invalidRecords?.length
                : invalidRecords?.length}
            </span>
          )}
          {!invalidRecords && (
            <span className='cursor-pointer hover:underline hover:text-primaryColor-normal'>
              Failed: {inviteStat?.failed}
            </span>
          )}
        </div>
        {!inviteStat?.pending ||
          (inviteStat?.pending > 0 && (
            <div
              className='my-4 flex gap-2 items-center'
              onClick={() => statusClickHandler('PENDING')}
            >
              <svg
                role='status'
                className='inline w-4 h-4 text-white animate-spin'
                viewBox='0 0 100 101'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                  fill='#F59E0B'
                />
                <path
                  d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                  fill='currentColor'
                />
              </svg>
              <span className='cursor-pointer hover:underline hover:text-primaryColor-normal'>
                Pending: {inviteStat?.pending ? inviteStat?.pending : 0}
              </span>
            </div>
          ))}
        {inviteStat?.pending === 0 && inviteStat?.processing === 0 && (
          <div className='my-4 flex gap-2 items-center'>
            <InformationCircleIcon className='h-5 w-5 text-secondaryColor-normal' />
            {invalidRecords && (
              <span>
                Completed: {inviteStat?.total + invalidRecords?.length}
              </span>
            )}
            {!invalidRecords && <span>Completed: {inviteStat?.total}</span>}
          </div>
        )}
      </div>
      <div className='my-3 border'>
        <table className='w-full relative flex flex-col h-80'>
          <thead className='bg-neutralColor-lighter'>
            <tr className='grid grid-cols-3'>
              <th
                className={`sticky p-3 top-0 text-neutralColor-medium text-left text-xs uppercase`}
              >
                Full Name
              </th>
              <th
                className={`sticky top-0 p-3 text-neutralColor-medium text-left text-xs uppercase col-span-2`}
              >
                Remarks
              </th>
            </tr>
          </thead>
          <tbody className='flex-1 overflow-y-auto divide-y divide-gray-300'>
            {(inviteStatusParams.status === undefined ||
              inviteStatusParams.status === 'FAILED') &&
              invalidRecords &&
              invalidRecords
                .slice(0, perPage)
                .map((ir: IInvalidRecordsArray, index: number) => (
                  <tr key={index} className='grid grid-cols-3'>
                    <td className='py-4 px-3 text-neutralColor-normal'>
                      Row {ir.row}
                    </td>
                    <td className='py-4 px-3 text-neutralColor-normal col-span-2'>
                      <div className='flex gap-2 items-start'>
                        <ExclamationIcon className='h-5 w-5 text-tertiaryColor-normal' />
                        <span className='flex-1'>
                          This entry is invalid because{' '}
                          <strong>{ir.col}</strong> value of{' '}
                          <strong>{ir.value} </strong>
                          is <strong>{ir.reason}</strong>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
            {((invalidRecords && invalidRecords?.length <= perPage) ||
              inviteStatusParams.status !== 'FAILED' ||
              !invalidRecords) &&
              inviteStatus?.map(
                (is: IGetBulkUserInviteStatusServiceResData) => {
                  return (
                    <tr className='grid grid-cols-3'>
                      <td className='py-4 px-3 text-neutralColor-normal'>
                        {is?.data?.name}
                      </td>
                      <td className='py-4 px-3 text-neutralColor-normal col-span-2'>
                        {is.status === 'COMPLETED' && (
                          <div className='flex gap-2 items-start'>
                            <CheckCircleIcon className='h-5 w-5 text-primaryColor-normal' />
                            <span className='flex-1'>
                              Invite Sent to <strong>{is?.data?.email}</strong>
                            </span>
                          </div>
                        )}
                        {is.status === 'FAILED' && (
                          <div className='flex gap-2 items-start'>
                            <ExclamationIcon className='h-5 w-5 text-tertiaryColor-normal' />
                            <span className='flex-1'>
                              {is.remarks.split(':')[1]}
                            </span>
                          </div>
                        )}
                        {is.status === 'PROCESSING' ||
                          (is.status === 'PENDING' && (
                            <div className='flex gap-2 items-start'>
                              <svg
                                role='status'
                                className='inline w-4 h-4 text-white animate-spin'
                                viewBox='0 0 100 101'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                  fill='#F59E0B'
                                />
                                <path
                                  d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                  fill='currentColor'
                                />
                              </svg>
                              <span className='flex-1'>
                                Sending Invitation to{' '}
                                <strong>{is?.data?.email}</strong>
                              </span>
                            </div>
                          ))}
                      </td>
                    </tr>
                  );
                }
              )}
          </tbody>
        </table>
        {inviteStat?.total > perPage && invalidRecords?.length > perPage && (
          <div
            className='flex justify-center items-center text-neutralColor-normal bg-neutralColor-lightest hover:bg-neutralColor-lighter cursor-pointer transition duration-400 ease-in-out'
            onClick={() => nextHandler()}
          >
            <span>More</span>
            <ChevronDownIcon className='h-6 w-6' />
          </div>
        )}
      </div>
      <div className='flex justify-end gap-4 mt-5'>
        <Button
          label='Close'
          btnStyle='btn-primary'
          onClick={() => handleModal('inviteNewUser')}
        />
      </div>
    </>
  );
}
